<template>
    <div class="contract-approval-page">
        <div class="top_change">
            <div class="l_where">
                系统设置<span>/</span>合同审批
            </div>
            <div class="r-actions">
                <Button type="primary" :loading="saving" @click="handleSave">保存配置</Button>
                <div class="r_back" @click="goBack">
                    <i class="iconfont icon-fanhui"></i>
                </div>
            </div>
        </div>

        <div class="approval-body">
            <!-- 左侧流程 -->
            <div class="flow-panel">
                <div class="flow-list" v-if="!pageLoading">
                    <!-- 申请人 -->
                    <div class="flow-node start-node">
                        <div class="node-card">
                            <div class="node-title">申请人</div>
                            <div class="node-desc">申请类型：合同审批申请</div>
                        </div>
                    </div>

                    <div
                        v-for="(node, index) in approveNodes"
                        :key="node._key"
                        class="flow-block"
                    >
                        <div class="flow-connector">
                            <div class="flow-line"></div>
                            <div class="add-btn" @click.stop="addNode(index)" title="添加审批人">
                                <Icon type="md-add" :size="14" />
                            </div>
                            <div class="flow-line"></div>
                            <div class="flow-arrow"></div>
                        </div>
                        <div
                            class="flow-node approve-node"
                            :class="{ active: selectedKey === node._key }"
                            @click="selectNode(node._key)"
                        >
                            <div class="node-card">
                                <div class="node-title">{{ node.name || '审批人' }}</div>
                                <div class="node-desc">{{ getNodeSummary(node) }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="flow-connector">
                        <div class="flow-line"></div>
                        <div class="add-btn" @click.stop="addNode(approveNodes.length)" title="添加审批人">
                            <Icon type="md-add" :size="14" />
                        </div>
                        <div class="flow-line"></div>
                        <div class="flow-arrow"></div>
                    </div>

                    <div class="flow-node end-node">
                        <div class="end-card">完成</div>
                    </div>
                </div>
                <div class="flow-loading" v-else>
                    <Spin fix></Spin>
                </div>
            </div>

            <!-- 右侧设置 -->
            <div class="setting-panel">
                <div class="setting-card force-card">
                    <div class="force-header">
                        <div class="force-title">合同审批强制开启</div>
                        <i-switch v-model="forceEnable" :true-value="1" :false-value="0" size="large">
                            <span slot="open">开启</span>
                            <span slot="close">关闭</span>
                        </i-switch>
                    </div>
                    <div class="force-desc">
                        企业所有合同（暂不支持解除协议）发起时，默认需要经由审批人审批通过后，才可发起成功并通知签署方。
                    </div>
                </div>

                <div class="setting-card node-card-setting" v-if="currentNode">
                    <div class="setting-title">审批节点设置</div>

                    <div class="form-item">
                        <div class="form-label">节点名称</div>
                        <Input v-model="currentNode.name" placeholder="请输入节点名称" maxlength="50" />
                    </div>

                    <div class="form-item">
                        <div class="form-label">选择类型</div>
                        <Select v-model="currentNode.selectType" style="width: 100%" @on-change="onSelectTypeChange">
                            <!-- 角色审批暂屏蔽，后续取消注释并设 enableRoleApprove=true 即可恢复 -->
                            <!-- <Option v-if="enableRoleApprove" value="role">角色审批</Option> -->
                            <Option value="member">成员审批</Option>
                        </Select>
                    </div>

                    <div class="form-item" v-if="enableRoleApprove && currentNode.selectType === 'role'">
                        <div class="form-label">选择角色</div>
                        <Select
                            v-model="currentNode.roleId"
                            style="width: 100%"
                            placeholder="请选择角色"
                            :loading="roleLoading"
                        >
                            <Option
                                v-for="item in roleOptions"
                                :value="item.value"
                                :key="item.value"
                            >{{ item.label }}</Option>
                        </Select>
                    </div>

                    <div class="form-item" v-else>
                        <div class="form-label">选择成员</div>
                        <Select
                            v-model="currentNode.members"
                            multiple
                            filterable
                            style="width: 100%"
                            placeholder="请选择成员"
                            :loading="memberLoading"
                            @on-query-change="onMemberQuery"
                        >
                            <Option
                                v-for="item in memberOptions"
                                :value="item.value"
                                :key="item.value"
                            >{{ item.label }}</Option>
                        </Select>
                    </div>

                    <div class="form-item">
                        <div class="form-label">多人审批时</div>
                        <RadioGroup v-model="currentNode.approveMode" vertical>
                            <Radio label="or">或签（一名审批人同意即可）</Radio>
                            <Radio label="and">会签（需所有审批人同意）</Radio>
                        </RadioGroup>
                    </div>

                    <Button
                        long
                        ghost
                        type="error"
                        class="del-btn"
                        :disabled="approveNodes.length <= 1"
                        :loading="deleting"
                        @click="deleteNode"
                    >删除审批人</Button>
                </div>

                <div class="setting-card empty-tip" v-else>
                    <div class="empty-text">请选择左侧审批节点进行设置</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    getContractApprovalConfig,
    saveContractApprovalNodes,
    delContractApprovalNode,
    getContractApprovalRoleList,
    getContractApprovalMemberList
} from '@/api/interFace'

let nodeSeed = 1

function createLocalNode (partial = {}) {
    return {
        _key: `n${nodeSeed++}`,
        id: 0,
        name: '审批人',
        selectType: 'member',
        roleId: '',
        members: [],
        approveMode: 'or',
        ...partial
    }
}

function parseMemberIds (val) {
    if (Array.isArray(val)) {
        return val.map(v => String(v)).filter(Boolean)
    }
    if (val == null || val === '') return []
    return String(val).split(',').map(s => s.trim()).filter(Boolean)
}

export default {
    name: 'ContractApproval',
    data() {
        return {
            // 角色审批开关：false 时仅成员审批；后续改为 true 即可恢复
            enableRoleApprove: false,
            pageLoading: false,
            saving: false,
            deleting: false,
            forceEnable: 0,
            selectedKey: '',
            approveNodes: [],
            roleOptions: [],
            memberOptions: [],
            roleLoading: false,
            memberLoading: false,
            memberQueryTimer: null
        }
    },
    computed: {
        currentNode() {
            return this.approveNodes.find(n => n._key === this.selectedKey) || null
        }
    },
    created() {
        this.$store.commit('changePollingSpin', true)
        this.initPage()
    },
    beforeDestroy() {
        clearTimeout(this.memberQueryTimer)
    },
    methods: {
        goBack() {
            this.$emit('goBack')
        },
        selectNode(key) {
            this.selectedKey = key
        },
        getRoleLabel(value) {
            const hit = this.roleOptions.find(r => String(r.value) === String(value))
            return hit ? hit.label : value
        },
        getMemberLabel(value) {
            const hit = this.memberOptions.find(m => String(m.value) === String(value))
            return hit ? hit.label : value
        },
        getNodeSummary(node) {
            const modeText = node.approveMode === 'and' ? '会签' : '或签'
            if (this.enableRoleApprove && node.selectType === 'role') {
                const name = this.getRoleLabel(node.roleId)
                return node.roleId ? `${modeText}：${name}` : `${modeText}：未选择角色`
            }
            const names = (node.members || []).map(v => this.getMemberLabel(v)).filter(Boolean)
            return names.length ? `${modeText}：${names.join('、')}` : `${modeText}：未选择成员`
        },
        onSelectTypeChange() {
            if (!this.currentNode) return
            if (!this.enableRoleApprove) {
                this.currentNode.selectType = 'member'
                this.currentNode.roleId = ''
                return
            }
            if (this.currentNode.selectType === 'role') {
                this.currentNode.members = []
                if (!this.currentNode.roleId && this.roleOptions.length) {
                    this.currentNode.roleId = this.roleOptions[0].value
                }
            } else {
                this.currentNode.roleId = ''
            }
        },
        addNode(index) {
            const node = createLocalNode({ selectType: 'member' })
            if (this.enableRoleApprove && this.roleOptions.length) {
                node.roleId = this.roleOptions[0].value
            }
            this.approveNodes.splice(index, 0, node)
            this.selectedKey = node._key
        },
        deleteNode() {
            if (this.approveNodes.length <= 1) {
                this.$Message.warning('至少保留一个审批节点')
                return
            }
            const idx = this.approveNodes.findIndex(n => n._key === this.selectedKey)
            if (idx < 0) return
            const node = this.approveNodes[idx]
            const serverId = Number(node.id) || 0

            // 未保存到服务端的本地节点：直接移除
            if (!serverId) {
                this.removeNodeAt(idx)
                return
            }

            this.$Modal.confirm({
                title: '删除审批节点',
                content: `确认删除节点「${node.name || '审批人'}」？删除后不再参与新的审批流程。`,
                loading: true,
                onOk: () => {
                    this.deleting = true
                    delContractApprovalNode({ id: serverId }).then(res => {
                        this.$Message.success(res.msg || '删除成功')
                        this.removeNodeAt(idx)
                        this.$Modal.remove()
                    }).catch(err => {
                        this.$Message.error((err && err.msg) || '删除失败')
                        this.$Modal.remove()
                    }).finally(() => {
                        this.deleting = false
                    })
                }
            })
        },
        removeNodeAt(idx) {
            this.approveNodes.splice(idx, 1)
            const next = this.approveNodes[Math.max(0, idx - 1)] || this.approveNodes[0]
            this.selectedKey = next ? next._key : ''
        },
        mapServerNode(item) {
            const type = Number(item.type)
            const mode = Number(item.approval_mode)
            // 角色审批屏蔽时，回显强制按成员审批编辑（保存时会写成 type=2）
            const isRole = this.enableRoleApprove && type === 1
            return createLocalNode({
                id: Number(item.id) || 0,
                name: item.name || '审批人',
                selectType: isRole ? 'role' : 'member',
                roleId: isRole ? (item.role_id ? String(item.role_id) : '') : '',
                members: isRole ? [] : parseMemberIds(item.member_ids),
                approveMode: mode === 2 ? 'and' : 'or'
            })
        },
        async initPage() {
            this.pageLoading = true
            try {
                const tasks = [this.loadMembers('')]
                if (this.enableRoleApprove) tasks.unshift(this.loadRoles())
                await Promise.all(tasks)
                await this.loadConfig()
            } finally {
                this.pageLoading = false
                this.$store.commit('changePollingSpin', false)
            }
        },
        loadRoles() {
            this.roleLoading = true
            return getContractApprovalRoleList().then(res => {
                const list = Array.isArray(res.data) ? res.data : []
                this.roleOptions = list.map(item => ({
                    value: String(item.id),
                    label: item.name || `角色${item.id}`
                }))
            }).catch(() => {
                this.roleOptions = [{ value: '3', label: '副管理员' }]
            }).finally(() => {
                this.roleLoading = false
            })
        },
        onMemberQuery(query) {
            clearTimeout(this.memberQueryTimer)
            this.memberQueryTimer = setTimeout(() => {
                this.loadMembers(query || '')
            }, 300)
        },
        loadMembers(title = '') {
            this.memberLoading = true
            return getContractApprovalMemberList({ title }).then(res => {
                const list = Array.isArray(res.data) ? res.data : []
                const map = new Map()
                // 保留已选项，避免筛选后丢失 label
                ;(this.memberOptions || []).forEach(item => {
                    map.set(String(item.value), item)
                })
                list.forEach(item => {
                    if (!item || item.id == null) return
                    const name = item.real_name || item.name || item.phone || `成员${item.id}`
                    const phone = item.phone ? `（${item.phone}）` : ''
                    map.set(String(item.id), {
                        value: String(item.id),
                        label: `${name}${phone}`
                    })
                })
                this.memberOptions = Array.from(map.values())
            }).catch(() => {
                // 保留已有 options
            }).finally(() => {
                this.memberLoading = false
            })
        },
        loadConfig() {
            return getContractApprovalConfig().then(res => {
                const data = res.data || {}
                this.forceEnable = Number(data.force_approval) === 1 ? 1 : 0
                const nodes = Array.isArray(data.nodes) ? data.nodes : []
                if (nodes.length) {
                    this.approveNodes = nodes.map(item => this.mapServerNode(item))
                } else {
                    this.approveNodes = [createLocalNode({ selectType: 'member' })]
                }
                this.selectedKey = this.approveNodes[0] ? this.approveNodes[0]._key : ''
            }).catch(err => {
                this.$Message.error((err && err.msg) || '获取审批配置失败')
                this.approveNodes = [createLocalNode()]
                this.selectedKey = this.approveNodes[0]._key
            })
        },
        validateBeforeSave() {
            if (!this.approveNodes.length) {
                this.$Message.warning('至少配置一个审批节点')
                return false
            }
            for (let i = 0; i < this.approveNodes.length; i++) {
                const node = this.approveNodes[i]
                const label = `第${i + 1}个节点`
                if (!(node.name || '').trim()) {
                    this.$Message.warning(`${label}请填写节点名称`)
                    this.selectedKey = node._key
                    return false
                }
                if (node.selectType === 'role' && this.enableRoleApprove) {
                    if (!node.roleId) {
                        this.$Message.warning(`${label}请选择角色`)
                        this.selectedKey = node._key
                        return false
                    }
                } else if (!node.members || !node.members.length) {
                    this.$Message.warning(`${label}请选择成员`)
                    this.selectedKey = node._key
                    return false
                }
            }
            return true
        },
        buildSavePayload() {
            return {
                force_approval: Number(this.forceEnable) === 1 ? 1 : 0,
                nodes: this.approveNodes.map(node => {
                    const item = {
                        id: Number(node.id) > 0 ? Number(node.id) : 0,
                        name: (node.name || '').trim(),
                        type: (this.enableRoleApprove && node.selectType === 'role') ? 1 : 2,
                        approval_mode: node.approveMode === 'and' ? 2 : 1
                    }
                    if (item.type === 1) {
                        item.role_id = Number(node.roleId) || 3
                    } else {
                        item.member_ids = (node.members || []).map(id => Number(id)).filter(n => !Number.isNaN(n))
                    }
                    return item
                })
            }
        },
        handleSave() {
            if (!this.validateBeforeSave()) return
            this.saving = true
            const payload = this.buildSavePayload()
            saveContractApprovalNodes(payload).then(res => {
                this.$Message.success(res.msg || '保存成功')
                return this.loadConfig()
            }).catch(err => {
                this.$Message.error((err && err.msg) || '保存失败')
            }).finally(() => {
                this.saving = false
            })
        }
    }
}
</script>

<style scoped lang="scss">
.contract-approval-page {
    width: 100%;
}

.top_change {
    width: 100%;
    background: #FFFFFF;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 30px;
    box-sizing: border-box;
    height: 60px;
    .l_where {
        font-size: 18px;
        font-family: PingFang SC;
        font-weight: bold;
        color: #1B2742;
        span {
            color: #4877E8;
        }
    }
    .r-actions {
        display: flex;
        align-items: center;
        gap: 16px;
    }
    .r_back {
        width: auto;
        cursor: pointer;
        text-align: end;
        .iconfont {
            font-size: 26px;
            cursor: pointer;
            &:hover {
                color: #4877E8;
            }
        }
    }
}

.approval-body {
    margin-top: 16px;
    display: flex;
    gap: 16px;
    height: calc(100vh - 230px);
    overflow: hidden;
}

.flow-panel {
    flex: 1;
    height: 100%;
    background: #F2F4F9;
    border-radius: 8px;
    padding: 48px 24px;
    display: flex;
    justify-content: center;
    overflow-x: hidden;
    overflow-y: auto;
    box-sizing: border-box;
    position: relative;
}

.flow-loading {
    position: relative;
    width: 100%;
    min-height: 200px;
}

.flow-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 260px;
}

.flow-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
}

.flow-connector {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2px 0;
}

.flow-line {
    width: 2px;
    height: 16px;
    background: #4C7AE8;
}

.flow-arrow {
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #4C7AE8;
    margin-bottom: 2px;
}

.add-btn {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #4C7AE8;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    flex-shrink: 0;
    &:hover {
        background: #3a68d4;
    }
}

.flow-node {
    width: 100%;
}

.node-card {
    background: #fff;
    border: 1px solid #E6EAF2;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(76, 122, 232, 0.08);
}

.node-title {
    height: 34px;
    line-height: 34px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 500;
    color: #333333;
    border-top: 3px solid transparent;
}

.node-desc {
    padding: 12px 14px 14px;
    font-size: 13px;
    color: #666666;
    line-height: 1.5;
    min-height: 42px;
    background: #fff;
    border-top: 1px solid #F0F2F5;
}

.start-node {
    .node-title {
        background: #E8F8F3;
        border-top-color: #3DB89A;
    }
}

.approve-node {
    cursor: pointer;
    .node-title {
        background: #EEF3FC;
        border-top-color: #4C7AE8;
    }
    .node-card {
        border-color: #4C7AE8;
    }
    &:not(.active) .node-card {
        border-color: #D7E2F8;
    }
    &.active .node-card {
        border-color: #4C7AE8;
        box-shadow: 0 2px 12px rgba(76, 122, 232, 0.18);
    }
}

.end-node {
    width: auto;
    .end-card {
        min-width: 108px;
        padding: 0 24px;
        height: 36px;
        line-height: 34px;
        text-align: center;
        border: 1px solid #4C7AE8;
        border-radius: 4px;
        color: #4C7AE8;
        font-size: 14px;
        background: #fff;
        box-shadow: 0 2px 8px rgba(76, 122, 232, 0.08);
    }
}

.setting-panel {
    width: 360px;
    flex-shrink: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    overflow-y: auto;
}

.setting-card {
    background: #fff;
    border-radius: 8px;
    padding: 20px;
}

.force-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.force-title {
    font-size: 16px;
    font-weight: 700;
    color: #1B2742;
}

.force-desc {
    font-size: 13px;
    color: #8A93A6;
    line-height: 1.7;
}

.setting-title {
    font-size: 16px;
    font-weight: 700;
    color: #1B2742;
    margin-bottom: 18px;
}

.form-item {
    margin-bottom: 18px;
}

.form-label {
    font-size: 14px;
    color: #1B2742;
    margin-bottom: 8px;
}

.del-btn {
    margin-top: 8px;
}

.empty-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

.empty-text {
    color: #8A93A6;
    font-size: 14px;
}
</style>
